import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { MonthCounts, ScheduleSession } from '../types/types';
import { useScheduleMonths } from '../hooks/useSchedule';
import { useLiveNow } from '../hooks/useLiveNow';
import { addDaysYmd, israelToday, isLiveNow, shiftYm, ymOf } from '../utils/schedule';
import { canPlayInApp, mergeLive } from '../utils/live';
import { useBreakpoint } from '../useBreakpoint';
import Grid from '../layout/Grid';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import { MOBILE_TOP_BAR_HEIGHT } from '../components/MobileShell';
import { buttonReset, color, font, radius, shadow, space, TAP, Tone, weight } from '../theme';
import PageHeader from '../components/PageHeader';
import TabBar from '../components/TabBar';
import Chip, { ChipRow } from '../components/Chip';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { ButtonLink } from '../components/Button';
import Icon from '../components/Icon';
import { EmptyState } from '../components/States';
import { SkeletonList } from '../components/Skeleton';
import { useScope } from '../hooks/useScope';
import { useScheduleNowSessions } from '../components/live/useOnAir';
import LiveNowSection, { NowRow } from '../components/live/LiveNowSection';
import { hhmm, RecordingButton } from '../components/live/PlayButtons';
import LiveBadge, { LiveState } from '../components/live/LiveBadge';

type TimeFilter = 'past' | 'today' | 'week' | 'month';
type TypeFilter = 'all' | 'committee' | 'plenum' | 'faction';

// ─── Knesset recess periods (update annually) ────────────────────────────────
const RECESS_PERIODS = [
  { start: '2026-04-11', end: '2026-04-27', label: 'פגרת פסח' },
  { start: '2026-07-31', end: '2026-10-25', label: 'פגרת קיץ' },
];

const recessInGap = (prev: string, next: string) =>
  RECESS_PERIODS.find(r => r.start > prev && r.start < next);

const gapDays = (d1: string, d2: string) =>
  Math.round((new Date(d2).getTime() - new Date(d1).getTime()) / 86400000);

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
};

const fmtDateLabel = (dateStr: string): { day: string; date: string } => {
  // "Today"/"tomorrow" are Israel dates; the local-midnight Date is only used for names
  const today = israelToday();
  const d = new Date(dateStr + 'T00:00:00');
  const datePart = d.toLocaleDateString('he-IL', { day: 'numeric', month: 'long' });
  if (dateStr === today) return { day: 'היום', date: datePart };
  if (dateStr === addDaysYmd(today, 1)) return { day: 'מחר', date: datePart };
  return { day: d.toLocaleDateString('he-IL', { weekday: 'long' }), date: datePart };
};

const dateKey = (iso: string) => iso.slice(0, 10);

// Committee and plenum ids can collide, so session keys always include the type
const sessionKey = (s: { type: string; id: number }) => `${s.type}-${s.id}`;

// Non-cancelled session count for a month under the active type filter
const countForFilter = (c: MonthCounts, typeFilter: TypeFilter): number => {
  if (typeFilter === 'committee') return c.committee;
  if (typeFilter === 'plenum') return c.plenum;
  if (typeFilter === 'all') return c.committee + c.plenum;
  return 0;
};

const fmtYearMonth = (ym: string): string => {
  const y = parseInt(ym.slice(0, 4), 10);
  const m = parseInt(ym.slice(5, 7), 10) - 1;
  return new Date(y, m, 1).toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });
};

// Calendar cells for a 'YYYY-MM' month: leading blanks up to the first weekday, then each date
const monthCells = (ym: string): (string | null)[] => {
  const y = parseInt(ym.slice(0, 4), 10);
  const m = parseInt(ym.slice(5, 7), 10) - 1;
  const firstDow = new Date(Date.UTC(y, m, 1)).getUTCDay(); // 0=Sun
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(`${ym}-${String(d).padStart(2, '0')}`);
  return cells;
};

// Lobbyists per committee member: high / medium / low (status, never clickable)
const lobbyTone = (lobbyistCount: number, memberCount: number): Tone | null => {
  if (!memberCount || !lobbyistCount) return null;
  const ratio = lobbyistCount / memberCount;
  if (ratio >= 3) return 'danger';
  if (ratio >= 1) return 'warning';
  return 'success';
};

// How busy a day is: the day header's side stripe, its count badge and the calendar dot
const dayBusyness = (count: number): { stripe: string; tone: Tone } => {
  if (count >= 10) return { stripe: '#E24B4A', tone: 'danger' };
  if (count >= 5) return { stripe: '#9a6a2a', tone: 'warning' };
  return { stripe: '#98a2b3', tone: 'neutral' };
};

const groupByDate = (sessions: ScheduleSession[]): Map<string, ScheduleSession[]> => {
  const map = new Map<string, ScheduleSession[]>();
  for (let i = 0; i < sessions.length; i++) {
    const key = dateKey(sessions[i].date);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(sessions[i]);
  }
  return map;
};

const countLabel = (n: number) => (n === 1 ? 'ישיבה אחת' : `${n} ישיבות`);

const https = (url: string) => url.replace(/^http:/, 'https:');

// ─── Pulsing dot animation via a tiny CSS keyframe injected once ────────────
const PULSE_STYLE = `
@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
}
`;

// Desktop: the month view is one large centred column — the calendar card, then the selected day
const CALENDAR_CARD: React.CSSProperties = {
  background: color.surface, borderRadius: radius.xl, boxShadow: shadow.card, padding: '20px 24px',
};

const MonthColumn = ({ children }: { children: React.ReactNode }) => {
  const { isDesktop } = useBreakpoint();
  return isDesktop ? <div style={{ maxWidth: '880px', margin: '0 auto' }}>{children}</div> : <>{children}</>;
};

const WEEKDAYS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

/* ── Day header ─────────────────────────────────────────────────────────── */

const DayHeader = ({ dateStr, count }: { dateStr: string; count: number }) => {
  const isToday = dateStr === israelToday();
  const { day, date } = fmtDateLabel(dateStr);
  const busy = dayBusyness(count);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, borderRight: `3px solid ${isToday ? color.primary : busy.stripe}`, paddingRight: space.sm + 2, marginBottom: space.sm + 2 }}>
      <span style={{ fontSize: font.label, fontWeight: weight.bold, color: isToday ? color.primary : color.text }}>{day}</span>
      <span style={{ fontSize: font.small, color: color.textSecondary }}>{date}</span>
      <Badge tone={isToday ? 'info' : busy.tone} shape="pill" style={{ padding: '0 8px', fontSize: font.caption }}>{count}</Badge>
    </div>
  );
};

/* ── Session card ───────────────────────────────────────────────────────── */

const SessionCard = ({ session, expanded, onToggle, inset = false, liveState = null }: {
  session: ScheduleSession;
  expanded: boolean;
  onToggle: () => void;
  // A lighter card inside another white surface (the past-month accordion)
  inset?: boolean;
  liveState?: LiveState;
}) => {
  const isCommittee = session.type === 'committee';
  const isFaction = session.type === 'faction';
  const name = (isCommittee || isFaction) ? session.committeeName : (session.sessionName || 'מליאה');
  const shownItems = expanded ? session.agendaItems : session.agendaItems.slice(0, 3);
  const extraCount = session.agendaItems.length - 3;
  const pressure = isCommittee ? lobbyTone(session.lobbyistCount || 0, session.memberCount || 0) : null;
  const linkTo = (isCommittee || isFaction) && session.committeeDocId ? `/committees/${session.committeeDocId}` : null;
  // A recording that plays here (decision 6A): past days' sessions only; the schedule rarely has one
  const day = dateKey(session.date);
  const recordingUrl = day < israelToday() && session.broadcastUrl && canPlayInApp(https(session.broadcastUrl)) ? https(session.broadcastUrl) : null;

  const title = (
    <>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.xs }}>
        <span style={{ fontSize: font.body, fontWeight: weight.bold, lineHeight: 1.35, color: color.text }}>{name}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: space.xs, flexWrap: 'wrap' }}>
          {isFaction ? <Badge>סיעה</Badge>
            : isCommittee ? <Badge tone="info">ועדה</Badge>
              : session.isSpecial ? <Badge tone="warning">ישיבה מיוחדת</Badge>
                : <Badge tone="special">מליאה</Badge>}
          {session.isCancelled && <Badge tone="danger">בוטלה</Badge>}
          {!session.isCancelled && <LiveBadge state={liveState} />}
          {isCommittee && session.sessionType && !session.isCancelled && <Badge>{session.sessionType}</Badge>}
          {pressure && !session.isCancelled && <Badge tone={pressure} dot={pressure === 'danger' ? '#E24B4A' : pressure === 'warning' ? '#9a6a2a' : '#3f7a16'}>{session.lobbyistCount} שדלנים</Badge>}
        </span>
      </span>
      <span style={{ fontSize: font.label, fontWeight: weight.bold, color: color.primary, flexShrink: 0 }}>{session.date ? fmtTime(session.date) : ''}</span>
      {linkTo && <span style={{ color: color.textTertiary, flexShrink: 0, alignSelf: 'center' }}><Icon name="chevronForward" size={18} /></span>}
    </>
  );
  const titleStyle: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: space.sm, textDecoration: 'none', color: color.text, minHeight: TAP };

  return (
    <div style={{
      background: inset ? color.surfaceMuted : color.surface,
      borderRadius: radius.lg + 2,
      boxShadow: inset ? 'none' : shadow.card,
      padding: space.lg,
      marginBottom: space.sm + 2,
      opacity: session.isCancelled ? 0.55 : 1,
    }}>
      {/* The header links to the committee; the agenda and the outside links sit below it */}
      {linkTo
        ? <Link to={linkTo} state={{ from: 'לוח הזמנים' }} style={titleStyle}>{title}</Link>
        : <div style={titleStyle}>{title}</div>}

      {shownItems.length > 0 && (
        <ol style={{ listStyle: 'none', margin: `${space.sm}px 0 0`, padding: 0 }}>
          {shownItems.map((item, i) => (
            <li key={i} style={{ display: 'flex', gap: space.sm, fontSize: font.small, lineHeight: 1.5, color: color.text, padding: `${space.xs + 1}px 0`, borderTop: `1px solid ${inset ? color.border : color.divider}` }}>
              <span style={{ fontSize: font.caption, color: color.textTertiary, flexShrink: 0, minWidth: 16, paddingTop: 1 }}>{item.ordinal}.</span>
              {item.billDocId
                ? <Link to={`/legislation/${item.billDocId}`} state={{ from: 'לוח הזמנים' }} style={{ flex: 1, color: color.primary, textDecoration: 'none' }}>{item.name}</Link>
                : <span style={{ flex: 1 }}>{item.name}</span>}
            </li>
          ))}
        </ol>
      )}
      {extraCount > 0 && (
        <button type="button" onClick={onToggle} aria-expanded={expanded} style={{ ...buttonReset, color: color.primary, fontSize: font.small, fontWeight: weight.medium, minHeight: 32 }}>
          {expanded ? 'הצגת פחות' : `ועוד ${extraCount} נושאים`}
        </button>
      )}

      {/* The protocol first (decision 6A), then the recording when it plays here, then the Knesset's pages */}
      {(session.protocolUrl || session.sessionUrl || session.broadcastUrl) && (
        <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', marginTop: space.sm }}>
          {session.protocolUrl && <ButtonLink href={https(session.protocolUrl)} icon="document">פרוטוקול</ButtonLink>}
          {recordingUrl && (
            <RecordingButton
              src={recordingUrl}
              title={name || ''}
              subtitle={`${new Date(`${day}T00:00:00`).toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' })} · ${hhmm(session.date)}`}
              agenda={session.agendaItems.length > 0 ? session.agendaItems[0].name : null}
              externalUrl={session.sessionUrl}
            />
          )}
          {session.sessionUrl && <ButtonLink href={https(session.sessionUrl)} icon="external" variant="neutral">עמוד הישיבה</ButtonLink>}
          {session.broadcastUrl && !recordingUrl && <ButtonLink href={https(session.broadcastUrl)} icon="video">שידור חי</ButtonLink>}
        </div>
      )}
    </div>
  );
};

/* ── Calendar ───────────────────────────────────────────────────────────── */

const CalendarGrid = ({ cells, grouped, selected, onSelect, today, large = false, dimPast = true }: {
  cells: (string | null)[];
  grouped: Map<string, ScheduleSession[]>;
  selected: string | null;
  onSelect: (dateStr: string | null) => void;
  today: string;
  large?: boolean;
  // Dim days before today (the month view); the past view dims empty days instead
  dimPast?: boolean;
}) => (
  <>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: `1px solid ${color.border}`, paddingBottom: space.xs + 2, marginBottom: space.xs }}>
      {WEEKDAYS.map(d => (
        <div key={d} style={{ textAlign: 'center', fontSize: large ? 17 : font.label, fontWeight: weight.bold, color: color.textSecondary, padding: '4px 0' }}>{d}</div>
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: large ? 6 : 3 }}>
      {cells.map((dateStr, idx) => {
        if (!dateStr) return <div key={`pad-${idx}`} />;
        const activeCount = (grouped.get(dateStr) || []).filter(s => !s.isCancelled).length;
        const isSelected = selected === dateStr;
        const isTodayCell = dateStr === today;
        const dim = dimPast ? dateStr < today && !isTodayCell : activeCount === 0;
        const busy = activeCount > 0 ? dayBusyness(activeCount) : null;
        const dayNum = parseInt(dateStr.slice(8), 10);
        return (
          <button
            key={dateStr}
            type="button"
            disabled={activeCount === 0}
            aria-pressed={isSelected}
            aria-label={`${fmtDateLabel(dateStr).date}, ${activeCount > 0 ? countLabel(activeCount) : 'אין ישיבות'}`}
            onClick={() => onSelect(isSelected ? null : dateStr)}
            style={{
              ...buttonReset,
              minHeight: large ? 84 : TAP,
              borderRadius: large ? radius.md : radius.sm + 2,
              padding: large ? '12px 6px 10px' : '6px 2px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: large ? 8 : 3,
              cursor: activeCount > 0 ? 'pointer' : 'default',
              background: isSelected ? color.primary : isTodayCell ? color.primarySoft : 'transparent',
              boxShadow: isTodayCell && !isSelected ? `inset 0 0 0 1px ${color.primary}` : 'none',
              opacity: dim ? 0.4 : 1,
            }}
          >
            <span style={{ fontSize: large ? 20 : font.label, fontWeight: isTodayCell || isSelected ? weight.bold : weight.regular, color: isSelected ? color.textOnPrimary : isTodayCell ? color.primary : color.text }}>{dayNum}</span>
            {busy && (large
              ? <Badge tone={isSelected ? 'info' : busy.tone} shape="pill" style={{ fontSize: font.caption, padding: '1px 8px' }}>{countLabel(activeCount)}</Badge>
              : <span style={{ width: 6, height: 6, borderRadius: '50%', background: isSelected ? color.textOnPrimary : busy.stripe }} />)}
          </button>
        );
      })}
    </div>
  </>
);

/* ── Page ───────────────────────────────────────────────────────────────── */

// Before a new Knesset's first plenum (once its term is in meta/scope): the days to go and
// the date, so a recess week with no sessions explains itself
const UpcomingKnesset = ({ today }: { today: string }) => {
  const scope = useScope();
  const israelDay = (iso: string) => new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'Asia/Jerusalem' });
  const upcoming = Object.keys(scope.knessets).map(Number)
    .filter(k => k > scope.knesset && israelDay(scope.knessets[String(k)].start) > today)
    .sort((a, b) => a - b);
  if (upcoming.length === 0) return null;
  const k = upcoming[0];
  const day = israelDay(scope.knessets[String(k)].start);
  const utc = (ymd: string) => Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)));
  const days = Math.round((utc(day) - utc(today)) / 86400000);
  const weekday = new Date(`${day}T12:00:00`).toLocaleDateString('he-IL', { weekday: 'short' });
  return (
    <Card style={{ marginTop: space.lg }}>
      <div style={{ display: 'flex', gap: space.sm }}>
        <div style={{ flex: 1, background: color.primarySoft, borderRadius: radius.lg, padding: space.md, textAlign: 'center' }}>
          <div style={{ fontSize: font.display, fontWeight: weight.bold, color: color.primaryInk, lineHeight: 1 }}>{days}</div>
          <div style={{ fontSize: font.caption, color: color.primaryInk, marginTop: space.xs }}>{days === 1 ? 'יום' : 'ימים'} להשבעת הכנסת ה-{k}</div>
        </div>
        <div style={{ flex: 1, background: color.surfaceMuted, borderRadius: radius.lg, padding: space.md, textAlign: 'center' }}>
          <div style={{ fontSize: font.display, fontWeight: weight.bold, color: color.text, lineHeight: 1 }}>{Number(day.slice(8, 10))}.{Number(day.slice(5, 7))}</div>
          <div style={{ fontSize: font.caption, color: color.textSecondary, marginTop: space.xs }}>{weekday} · ישיבת הפתיחה</div>
        </div>
      </div>
      <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5, marginTop: space.md }}>
        עד אז המליאה בפגרה. אחרי ההשבעה יופיעו כאן ישיבות הכנסת ה-{k}, והוועדות שלה יוקמו בשבועות שאחריה.
      </div>
    </Card>
  );
};

const SchedulePage: React.FC = () => {
  const { isDesktop } = useBreakpoint();
  // Everything is relative to the Israel date, whatever the browser's time zone
  const today = israelToday();
  const weekEnd = addDaysYmd(today, 7);
  const currentYm = ymOf(today);
  const nextYm = shiftYm(currentYm, 1);
  const { sessions, loadedMonths, ensureMonth, index, loading, lastSynced } = useScheduleMonths([currentYm, nextYm]);
  // What's on air: the live feed (re-read every minute) merged with meta/scheduleNow
  const nowSessions = useScheduleNowSessions();
  const liveNow = useLiveNow();
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('week');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [openMonths, setOpenMonths] = useState<Set<string>>(new Set());
  const [selectedPastDay, setSelectedPastDay] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [calYm, setCalYm] = useState<string>(currentYm);

  const toggleMonth = (ym: string) => {
    ensureMonth(ym);
    setOpenMonths(prev => {
      const next = new Set(prev);
      if (next.has(ym)) next.delete(ym); else next.add(ym);
      return next;
    });
  };

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = PULSE_STYLE;
    document.head.appendChild(styleEl);
    return () => { document.head.removeChild(styleEl); };
  }, []);

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectTimeFilter = (key: TimeFilter) => {
    setTimeFilter(key);
    if (key === 'past') { setOpenMonths(new Set()); setSelectedPastDay(null); }
    if (key === 'month') { setSelectedDay(today); setCalYm(currentYm); }
  };

  // ── Derived values ────────────────────────────────────────────────────────

  // Months that have docs (ascending); the month tab can't navigate past them
  const indexMonths = Object.keys(index ? index.months : {}).sort();
  const minYm = indexMonths.length > 0 && indexMonths[0] < currentYm ? indexMonths[0] : currentYm;
  const maxYm = indexMonths.length > 0 && indexMonths[indexMonths.length - 1] > currentYm ? indexMonths[indexMonths.length - 1] : currentYm;

  const goToMonth = (ym: string) => {
    if (ym < minYm || ym > maxYm) return;
    setCalYm(ym);
    ensureMonth(ym);
    // Back on the current month → today; any other month starts with no day selected
    setSelectedDay(ym === currentYm ? today : null);
  };

  const calMonthLoading = !loadedMonths.has(calYm);

  // The loaded month sessions from yesterday to tomorrow, by key: their agenda items for the
  // live cards, and their sessionType for a closed session's "not aired" reason
  const yesterday = addDaysYmd(today, -1);
  const tomorrow = addDaysYmd(today, 1);
  const nearByKey = useMemo(() => {
    const byKey: Record<string, ScheduleSession> = {};
    for (let i = 0; i < sessions.length; i++) {
      const d = dateKey(sessions[i].date);
      if (d >= yesterday && d <= tomorrow) byKey[sessionKey(sessions[i])] = sessions[i];
    }
    return byKey;
  }, [sessions, yesterday, tomorrow]);
  const nowRows = useMemo((): NowRow[] => nowSessions.map(r => {
    const full = nearByKey[sessionKey(r)];
    return full && full.sessionType ? { ...r, sessionType: full.sessionType } : r;
  }), [nowSessions, nearByKey]);
  const { live, clock, fresh } = liveNow;
  const merged = useMemo(() => mergeLive(nowRows, live, clock, { fresh }), [nowRows, live, clock, fresh]);
  // Wait for the feed's first read, so the section doesn't switch from the time rule to the feed
  const liveReady = live !== undefined || liveNow.error;
  const liveStateOf = (s: ScheduleSession): LiveState => {
    if (!liveReady) return null;
    const key = sessionKey(s);
    if (merged.pausedKeys.has(key)) return 'paused';
    if (merged.keys.has(key)) return 'live';
    // Without the feed, the time rule (also before meta/scheduleNow is read)
    return !merged.fromFeed && isLiveNow(s) ? 'live' : null;
  };

  // Summary line: from the current + next month, which are always loaded
  const upcoming = sessions.filter(s => {
    const ym = s.date.slice(0, 7);
    return ym === currentYm || ym === nextYm;
  });
  const todayCount = upcoming.filter(s => dateKey(s.date) === today && !s.isCancelled).length;
  const weekCount = upcoming.filter(s => dateKey(s.date) >= today && dateKey(s.date) <= weekEnd && !s.isCancelled).length;
  const broadcastCount = upcoming.filter(s => s.broadcastUrl && dateKey(s.date) >= today).length;

  const timeFiltered = sessions.filter(s => {
    const key = dateKey(s.date);
    if (timeFilter === 'past') return key < today;
    if (timeFilter === 'today') return key === today;
    if (timeFilter === 'week') return key >= today && key <= weekEnd;
    return key.slice(0, 7) === calYm;
  });
  const filtered = timeFiltered.filter(s => (typeFilter === 'all' ? true : s.type === typeFilter));

  // Past sessions shown newest-first
  const orderedFiltered = timeFilter === 'past' ? filtered.slice().reverse() : filtered;
  const grouped = groupByDate(orderedFiltered);

  // Past months (newest first): the current month if it already has past sessions,
  // then every earlier month in the index that has sessions for the active filter
  const currentMonthPast = filtered.filter(s => s.date.slice(0, 7) === currentYm);
  const pastMonths: string[] = [];
  if (timeFilter === 'past') {
    if (currentMonthPast.length > 0) pastMonths.push(currentYm);
    for (let i = indexMonths.length - 1; i >= 0; i--) {
      const ym = indexMonths[i];
      if (ym >= currentYm || !index) continue;
      const counts = index.months[ym];
      if (countForFilter(counts, typeFilter) > 0 || (typeFilter === 'all' && counts.cancelled > 0)) pastMonths.push(ym);
    }
  }

  // Header count: index counts for whole past months; the current month only up to yesterday
  const pastMonthCount = (ym: string): number => {
    if (ym === currentYm) return currentMonthPast.filter(s => !s.isCancelled).length;
    return index && index.months[ym] ? countForFilter(index.months[ym], typeFilter) : 0;
  };

  const renderDaySessions = (daySessions: ScheduleSession[], inset = false) =>
    daySessions.map(session => (
      <SessionCard key={sessionKey(session)} session={session} inset={inset} liveState={liveStateOf(session)} expanded={expanded.has(sessionKey(session))} onToggle={() => toggleExpand(sessionKey(session))} />
    ));

  const summary: React.ReactNode[] = [];
  if (todayCount > 0) summary.push(<span key="t">{countLabel(todayCount)} היום</span>);
  if (weekCount > todayCount) summary.push(<span key="w">{weekCount} השבוע</span>);
  if (broadcastCount > 0) summary.push(<span key="b" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="video" size={15} />{broadcastCount} עם שידור חי</span>);

  return (
    <Layout width="full">
      <PageContainer>
        <div style={isDesktop ? undefined : { padding: `${space.sm}px ${space.lg}px 0` }}>
          <PageHeader
            title="לוח הזמנים"
            subtitle={!loading && summary.length > 0
              ? <span style={{ display: 'flex', gap: space.md, flexWrap: 'wrap' }}>{summary}</span>
              : 'ישיבות הוועדות והמליאה'}
          />
          <UpcomingKnesset today={today} />
        </div>

        {/* Tabs and type filters (phone/tablet: stick under the top bar while scrolling) */}
        <div style={isDesktop
          ? { padding: `${space.lg}px 0`, display: 'flex', flexDirection: 'column', gap: space.sm }
          : { position: 'sticky', top: MOBILE_TOP_BAR_HEIGHT, zIndex: 10, background: 'rgba(238,242,247,0.94)', padding: `${space.md}px ${space.lg}px ${space.xs}px`, display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <TabBar
            label="טווח זמן"
            tabs={[{ key: 'past', label: 'עבר' }, { key: 'today', label: 'היום' }, { key: 'week', label: 'השבוע' }, { key: 'month', label: 'החודש' }]}
            active={timeFilter}
            onChange={selectTimeFilter}
            style={isDesktop ? { maxWidth: 480 } : undefined}
          />
          <ChipRow>
            <Chip selected={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>הכל</Chip>
            <Chip selected={typeFilter === 'committee'} onClick={() => setTypeFilter('committee')}>ועדות</Chip>
            <Chip selected={typeFilter === 'plenum'} onClick={() => setTypeFilter('plenum')}>מליאה</Chip>
          </ChipRow>
        </div>

        <div style={isDesktop ? undefined : { padding: `${space.sm}px ${space.lg}px ${space.lg}px` }}>

          {/* ── Live now: on air, then "לפי הלו״ז, בלי שידור" (desktop: side by side) ── */}
          {liveReady && timeFilter !== 'past' && (
            <LiveNowSection merged={merged} fullSession={key => nearByKey[key]} />
          )}

          {/* ── Main content ─────────────────────────────────────────────── */}
          {loading ? (
            <div data-skeleton="true"><SkeletonList rows={5} /></div>
          ) : timeFilter === 'month' ? (
            // Desktop: a large calendar centred on the page, the selected day below it
            <MonthColumn>
              <div style={isDesktop ? CALENDAR_CARD : { background: color.surface, borderRadius: radius.xl, boxShadow: shadow.card, padding: space.md }}>
                {/* Month navigation: previous on the right, next on the left (RTL) */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.md }}>
                  <button type="button" aria-label="החודש הקודם" disabled={calYm <= minYm} onClick={() => goToMonth(shiftYm(calYm, -1))}
                    style={{ ...buttonReset, width: TAP, height: TAP, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: color.primary, background: color.primarySoft, opacity: calYm <= minYm ? 0.35 : 1, cursor: calYm <= minYm ? 'default' : 'pointer' }}>
                    <Icon name="chevronBack" size={20} strokeWidth={2.2} />
                  </button>
                  <span style={{ fontSize: isDesktop ? 20 : font.heading, fontWeight: weight.bold }}>{fmtYearMonth(calYm)}</span>
                  <button type="button" aria-label="החודש הבא" disabled={calYm >= maxYm} onClick={() => goToMonth(shiftYm(calYm, 1))}
                    style={{ ...buttonReset, width: TAP, height: TAP, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: color.primary, background: color.primarySoft, opacity: calYm >= maxYm ? 0.35 : 1, cursor: calYm >= maxYm ? 'default' : 'pointer' }}>
                    <Icon name="chevronForward" size={20} strokeWidth={2.2} />
                  </button>
                </div>
                {calMonthLoading && <div style={{ textAlign: 'center', fontSize: font.small, color: color.textSecondary, marginBottom: space.sm }}>טוען את החודש…</div>}
                <CalendarGrid cells={monthCells(calYm)} grouped={grouped} selected={selectedDay} onSelect={setSelectedDay} today={today} large={isDesktop} />
              </div>

              {/* ── Selected day sessions ── */}
              <div style={{ marginTop: isDesktop ? space.xxl : space.xl }}>
                {selectedDay && grouped.has(selectedDay) ? (
                  <>
                    <DayHeader dateStr={selectedDay} count={grouped.get(selectedDay)!.filter(s => !s.isCancelled).length} />
                    {/* Desktop: the day's sessions two across under the calendar */}
                    <Grid onlyOnDesktop minColWidth={380} gap="10px" rowGap={0}>
                      {renderDaySessions(grouped.get(selectedDay)!)}
                    </Grid>
                  </>
                ) : (
                  <p style={{ textAlign: 'center', margin: 0, fontSize: font.small, color: color.textSecondary }}>
                    {selectedDay ? 'אין ישיבות ביום זה' : 'בחרו יום בלוח כדי לראות את הישיבות'}
                  </p>
                )}
              </div>
            </MonthColumn>
          ) : timeFilter === 'past' ? (
            /* ── Past: month accordion with a calendar ── */
            pastMonths.length === 0
              ? <Card padding={0}><EmptyState icon="calendar" title="אין ישיבות בעבר" /></Card>
              : pastMonths.map(ym => {
                const isOpen = openMonths.has(ym);
                const monthLoaded = loadedMonths.has(ym);
                return (
                  <Card key={ym} padding={0} style={{ marginBottom: space.sm + 2 }}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggleMonth(ym)}
                      style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: space.sm, minHeight: 52, padding: `0 ${space.lg}px` }}
                    >
                      <span style={{ flex: 1, fontSize: font.body, fontWeight: weight.bold }}>{fmtYearMonth(ym)}</span>
                      <span style={{ fontSize: font.small, color: color.textSecondary }}>{countLabel(pastMonthCount(ym))}</span>
                      <span style={{ color: color.textTertiary }}><Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={18} /></span>
                    </button>

                    {isOpen && !monthLoaded && <div style={{ padding: `0 ${space.lg}px ${space.lg}px` }}><SkeletonList rows={2} card={false} /></div>}
                    {isOpen && monthLoaded && (
                      <div style={{ padding: `0 ${space.md}px ${space.md}px`, borderTop: `1px solid ${color.divider}`, paddingTop: space.md }}>
                        {/* Desktop: the month's calendar beside the selected day */}
                        <TwoPane sideWidth={340} stickySide={false} side={
                          <CalendarGrid cells={monthCells(ym)} grouped={grouped} selected={selectedPastDay} onSelect={setSelectedPastDay} today={today} dimPast={false} />
                        }>
                          {selectedPastDay && selectedPastDay.startsWith(ym) ? (
                            <div style={isDesktop ? undefined : { marginTop: space.lg, borderTop: `1px solid ${color.divider}`, paddingTop: space.md }}>
                              <DayHeader dateStr={selectedPastDay} count={(grouped.get(selectedPastDay) || []).filter(s => !s.isCancelled).length} />
                              {/* Desktop: the day's sessions two across beside the calendar */}
                              <Grid onlyOnDesktop minColWidth={300} gap="10px" rowGap={0}>
                                {renderDaySessions(grouped.get(selectedPastDay) || [], true)}
                              </Grid>
                            </div>
                          ) : isDesktop ? (
                            <p style={{ textAlign: 'center', margin: `${space.lg}px 0 0`, fontSize: font.small, color: color.textSecondary }}>בחרו יום בלוח כדי לראות את הישיבות</p>
                          ) : null}
                        </TwoPane>
                      </div>
                    )}
                  </Card>
                );
              })
          ) : grouped.size === 0 ? (
            <Card padding={0}><EmptyState icon="calendar" title="אין ישיבות מתוכננות" body={timeFilter === 'today' ? 'אין ישיבות היום. נסו את לשונית "השבוע".' : undefined} /></Card>
          ) : (
            Array.from(grouped.entries()).map(([dateStr, daySessions], idx, arr) => {
              const prevDate = idx > 0 ? arr[idx - 1][0] : null;
              const recess = prevDate && gapDays(prevDate, dateStr) >= 4 ? recessInGap(prevDate, dateStr) : null;
              return (
                <React.Fragment key={dateStr}>
                  {recess && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: space.sm + 2, margin: `${space.sm}px 0 ${space.lg}px` }}>
                      <div style={{ flex: 1, height: 1, background: color.border }} />
                      <Badge shape="pill"><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="calendar" size={14} />{recess.label}</span></Badge>
                      <div style={{ flex: 1, height: 1, background: color.border }} />
                    </div>
                  )}
                  <div style={{ marginBottom: space.xl }}>
                    <DayHeader dateStr={dateStr} count={daySessions.filter(s => !s.isCancelled).length} />
                    {/* Session cards (desktop: as many 380px+ columns as fit) */}
                    <Grid onlyOnDesktop minColWidth={380} gap="10px" rowGap={0}>
                      {renderDaySessions(daySessions)}
                    </Grid>
                  </div>
                </React.Fragment>
              );
            })
          )}

          {lastSynced && (
            <div style={{ textAlign: 'center', fontSize: font.caption, color: color.textSecondary, margin: `${space.sm}px 0` }}>
              עודכן לאחרונה: {new Date(lastSynced).toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Jerusalem' })}
            </div>
          )}
        </div>
      </PageContainer>
    </Layout>
  );
};

export default SchedulePage;
